
import { useState } from 'react';

export default function ServicesQuoteForm() {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(false);

    const form = e.currentTarget;
    const formData = new FormData(form);
    const urlEncodedData = new URLSearchParams();
    formData.forEach((value, key) => {
      urlEncodedData.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d6b6av5bd17lru29m860', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncodedData.toString(),
      });

      if (response.ok) {
        setSubmitted(true);
        form.reset();
      } else {
        throw new Error('Failed');
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/b94a4567429c924df6637596479f9697.jpg"
          alt="Get a Quote Background"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/75 via-black/60 to-black/75"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <h2 className="text-5xl font-bold text-white mb-5 tracking-tight leading-tight">
              Ready to Start<br />Your Project?
            </h2>
            <p className="text-lg text-white/85 leading-relaxed mb-8">
              Fill out the form and our team will get back to you within 24 hours with a free, no-obligation quote.
            </p>

            <div className="space-y-5 mb-8">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-500/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-fill text-amber-400 text-xl"></i>
                </div>
                <div>
                  <p className="text-white/60 text-xs font-medium uppercase tracking-wider">Call Us</p>
                  <a href="tel:7805178242" className="text-white font-bold text-lg hover:text-amber-400 transition-colors cursor-pointer">780-517-8242</a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-500/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-fill text-amber-400 text-xl"></i>
                </div>
                <div>
                  <p className="text-white/60 text-xs font-medium uppercase tracking-wider">Email Us</p>
                  <a href="mailto:Info@sierrakustoms.ca" className="text-white font-bold text-lg hover:text-amber-400 transition-colors cursor-pointer">Info@sierrakustoms.ca</a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-500/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-fill text-amber-400 text-xl"></i>
                </div>
                <div>
                  <p className="text-white/60 text-xs font-medium uppercase tracking-wider">Serving</p>
                  <p className="text-white font-bold text-base">Edmonton &amp; Surrounding Alberta</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 shadow-2xl">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                  <i className="ri-check-line text-green-600 text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Quote Request Sent!</h3>
                <p className="text-gray-600 text-sm mb-6">We&apos;ll get back to you within 24 hours.</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="text-amber-600 font-semibold text-sm hover:text-amber-700 cursor-pointer whitespace-nowrap"
                >
                  Submit Another Request
                </button>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">Request a Free Quote</h3>
                <p className="text-gray-500 text-sm mb-6">No obligation — we&apos;ll respond within 24 hours</p>

                <form
                  id="services-quote-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="sq-name" className="block text-xs font-bold text-gray-700 mb-1.5">Full Name *</label>
                      <input type="text" id="sq-name" name="name" required placeholder="John Smith" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-amber-500 transition-colors" />
                    </div>
                    <div>
                      <label htmlFor="sq-phone" className="block text-xs font-bold text-gray-700 mb-1.5">Phone *</label>
                      <input type="tel" id="sq-phone" name="phone" required placeholder="(780) 123-4567" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-amber-500 transition-colors" />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="sq-email" className="block text-xs font-bold text-gray-700 mb-1.5">Email *</label>
                    <input type="email" id="sq-email" name="email" required placeholder="john@example.com" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-amber-500 transition-colors" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="sq-service" className="block text-xs font-bold text-gray-700 mb-1.5">Service *</label>
                      <select id="sq-service" name="service" required className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-amber-500 transition-colors cursor-pointer">
                        <option value="">Select a service</option>
                        <option value="Trim & Door Installation">Trim &amp; Door Installation</option>
                        <option value="Premium Flooring">Premium Flooring</option>
                        <option value="Window Installation">Window Installation</option>
                        <option value="Fencing">Fencing</option>
                        <option value="Custom Deck Building">Custom Deck Building</option>
                        <option value="Residential Cleaning">Residential Cleaning</option>
                        <option value="Office Cleaning">Office Cleaning</option>
                        <option value="Multiple Services">Multiple Services</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="sq-location" className="block text-xs font-bold text-gray-700 mb-1.5">Location *</label>
                      <select id="sq-location" name="location" required className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-amber-500 transition-colors cursor-pointer">
                        <option value="">Select location</option>
                        <option value="Edmonton">Edmonton</option>
                        <option value="Edson">Edson</option>
                        <option value="Hinton">Hinton</option>
                        <option value="Whitecourt">Whitecourt</option>
                        <option value="Spruce Grove">Spruce Grove</option>
                        <option value="Mexico">Mexico</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="sq-message" className="block text-xs font-bold text-gray-700 mb-1.5">Project Details *</label>
                    <textarea id="sq-message" name="message" required maxLength={500} rows={4} placeholder="Tell us about your project... (max 500 characters)" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-amber-500 transition-colors resize-none"></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-amber-600 to-amber-500 text-white px-8 py-3.5 rounded-lg hover:from-amber-700 hover:to-amber-600 transition-all cursor-pointer font-bold text-sm flex items-center justify-center gap-2 whitespace-nowrap shadow-lg shadow-amber-600/30 disabled:opacity-60"
                  >
                    {loading ? (
                      <><i className="ri-loader-4-line animate-spin text-lg"></i> Sending...</>
                    ) : (
                      <>Send Quote Request <i className="ri-send-plane-fill text-base"></i></>
                    )}
                  </button>

                  {error && (
                    <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm font-medium text-center">
                      Failed to send. Please try calling us at 780-517-8242.
                    </div>
                  )}
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
